import { Injectable } from '@angular/core';
import { firstValueFrom, from, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { MasterService } from '../../../master/services/master.service';
import { bankAccountOptions, EntityFormOption } from '../../../finance/entity-form/entity-form.utils';
import { PolizaCreditoService } from '../../../../services/finanzas/poliza-credito/poliza-credito.service';
import { FinancialInvestmentEntityService } from './financial-investment-entity.service';

export interface FinancialInvestmentAccountOption extends EntityFormOption {
  accountingModelsId: number;
  number: string;
  name: string;
}

export type FinancialPayLookupKind = 'bankAccounts' | 'creditCards' | 'cash' | 'partners' | 'creditPolicies';

const CREDIT_CARD_TYPE = 1;

@Injectable({ providedIn: 'root' })
export class FinancialInvestmentLookupsFacade {

  private readonly payCache = new Map<FinancialPayLookupKind, Observable<EntityFormOption[]>>();

  constructor(
    private masterService: MasterService,
    private entity: FinancialInvestmentEntityService,
    private polizaCreditoService: PolizaCreditoService,
  ) { }

  accounts(): Promise<FinancialInvestmentAccountOption[]> {
    return this.entity.accounts().then(list => (list || []).map(account => ({
      id: account.number,
      display: [account.number, account.name].filter(Boolean).join(' · '),
      accountingModelsId: Number(account.id) || 0,
      number: account.number,
      name: account.name,
    })));
  }

  payOptions(kind: FinancialPayLookupKind): Promise<EntityFormOption[]> {
    let entry = this.payCache.get(kind);
    if (!entry) {
      entry = this.requestPayOptions(kind).pipe(
        catchError(error => {
          this.payCache.delete(kind);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.payCache.set(kind, entry);
    }
    return firstValueFrom(entry.pipe(map(list => list.slice())));
  }

  private requestPayOptions(kind: FinancialPayLookupKind): Observable<EntityFormOption[]> {
    switch (kind) {
      case 'bankAccounts':
        return this.masterService.getBankAccounts().pipe(map(list => bankAccountOptions(list)));
      case 'creditCards':
        return this.masterService.getPaymentCards(CREDIT_CARD_TYPE).pipe(map(list => (list || []).map(card => ({ id: card.id, display: card.display }))));
      case 'cash':
        return this.masterService.getCashRegisters().pipe(map(list => (list || [])
          .filter(item => item.metadata && item.metadata.id)
          .map(item => ({ id: item.metadata.id, display: item.display }))));
      case 'partners':
        return this.masterService.getParnerts().pipe(map(list => (list || []).map(partner => ({ id: Number(partner.id), display: partner.display }))));
      default:
        return from(this.polizaCreditoService.listPayments()).pipe(
          map((list: any[]) => (list || []).map(policy => ({ id: policy.uid, display: [policy.banco, policy.iban].filter(Boolean).join(' · ') || 'Póliza de crédito' }))),
        );
    }
  }
}
